// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { ellipsis } from "@steiner/XDL/Utilities";
import styled, { css } from "styled-components";

export interface DataTableCellBaseContextData {
    /** How the content should align within the cell. */
    alignX?: "left" | "center" | "right";
    /** Sets the cell/column width to be as narrow as possible. */
    tight?: boolean;
}

export type DataTableCellBaseProps = MobiusStyledComponentProps<"td", DataTableCellBaseContextData & InjectableCss>;

const defaultStyle = css``;

const mobileTableStyles = css`
    ${injectCss}
    display: grid;
    grid-template-columns: 1fr 1fr;
    justify-content: space-between;
    align-items: flex-start;
    column-gap: 12px;
    > ${StyledButton} {
        display: block;
        ${ellipsis(5)}
        ${TypographyStyle} {
            font-size: ${fontConfig.fontSize.small}px;
            line-height: ${fontConfig.lineHeight.small};
            font-weight: ${fontConfig.weight.bold};
            text-align: left;
        }
    }
    padding: 0;
    max-width: unset;
    width: 100%;
    page-break-inside: avoid;
`;

const DataTableCellBase = styled.td<
    DataTableCellBaseContextData &
        InjectableCss & { isMobile?: boolean; useMobileLayout?: boolean; usePrintLayout?: boolean } // XNG-Changes
>`
    ${({ isMobile, useMobileLayout, alignX = "left", tight }) => {
        if (isMobile && useMobileLayout) {
            return mobileTableStyles;
        }
        return css`
            box-sizing: border-box;
            height: 50px;
            padding: 0 12px;
            border-bottom: 1px solid ${getColor("common.border")};
            white-space: nowrap;
            ${tight && "width: 1px;"}
            ${alignX && `text-align: ${alignX};`};
        `;
    }}

    ${({ usePrintLayout }) =>
        usePrintLayout &&
        css`
            @media print {
                ${mobileTableStyles}
            }
        `}
    ${injectCss}
`;

/**
 * XNG-Changes
 */
export const HeaderCellTypography = styled.p<{ isMobile?: boolean; useMobileLayout?: boolean }>`
    font-size: ${fontConfig.fontSize.small}px;
    line-height: 22px;
    font-weight: ${fontConfig.weight.bold};
    white-space: pre-wrap;
    text-align: left;

    display: none;

    ${({ isMobile, useMobileLayout }) =>
        isMobile &&
        useMobileLayout &&
        css`
            display: block;
        `}

    @media print {
        display: block;
    }
`;

export default DataTableCellBase;
